import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext, createRoute, createRouter, Outlet, redirect,
} from '@tanstack/react-router';
import { Spinner } from '@dnx/ui';
import { AppShell } from './components/app-shell.js';
import { AccountPage } from './routes/account.js';
import { InboxPage, type InboxSearch } from './inbox/inbox-page.js';
import { WhatsAppSimulatorPage } from './dev/whatsapp-simulator.js';
import { LeadsPage, type LeadsSearch } from './crm/leads-page.js';
import { AgendaPage, type AgendaSearch } from './agenda/agenda-page.js';
import { KbPage } from './kb/kb-page.js';
import { HomePage } from './routes/home.js';
import { LoginPage } from './routes/login.js';
import { MfaSetupPage } from './routes/mfa-setup.js';
import { MfaVerifyPage } from './routes/mfa-verify.js';
import { RecoveryCodesPage } from './routes/recovery-codes.js';
import { SelectOrganizationPage } from './routes/select-organization.js';
import { isAuthError, meQuery, pendingStep } from './lib/session.js';
import { isAdminHost } from './lib/host.js';
import { type DetailTab, OrganizationDetailPage } from './platform/organization-detail.js';
import { OrganizationNewPage } from './platform/organization-new.js';
import { OrganizationsListPage } from './platform/organizations-list.js';
import { orgQuery, orgsQuery } from './platform/queries.js';
import { NoPlatformAccess, PlatformShell } from './platform/shell.js';
import { EmailSettingsPage, emailSettingsQuery } from './platform/settings-email.js';
import { AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage } from './routes/account-links.js';
import { TeamSettingsPage } from './settings/team-page.js';
import { BrandSettingsPage } from './settings/brand-page.js';
import { DistributionSettingsPage } from './settings/distribution-page.js';
import { SettingsLayout } from './settings/layout.js';
import { QuickRepliesSettingsPage } from './settings/quick-replies-page.js';
import { TagsSettingsPage } from './settings/tags-page.js';
import { AutomationEditorPage } from './automations/editor-page.js';
import { AutomationsPage } from './automations/list-page.js';
import { BroadcastPage, BroadcastsPage } from './broadcasts/pages.js';
import { PatientPage, type PatientTab } from './clinical/patient-page.js';
import { PatientsPage } from './clinical/patients-page.js';
import { NewQuotePage, QuotePage } from './billing/quote-page.js';
import { QuotesPage } from './billing/quotes-page.js';
import { ChargesPage } from './billing/charges.js';
import { PaymentsSettingsPage } from './settings/payments-page.js';
import { ChannelsSettingsPage } from './settings/channels-page.js';
import { ConversionsSettingsPage } from './settings/conversions-page.js';
import { FormsSettingsPage } from './settings/forms-page.js';
import { UnitSettingsPage } from './settings/unit-page.js';
import { SurgeriesPage } from './surgery/surgeries-page.js';
import { PostopPage } from './postop/postop-page.js';
import { PostopSettingsPage } from './settings/postop-page.js';
import { PricesSettingsPage } from './settings/prices-page.js';
import { DocumentsSettingsPage } from './settings/documents-page.js';
import { SignaturePrintPage } from './documents/shared.js';

interface Ctx { queryClient: QueryClient }

const rootRoute = createRootRouteWithContext<Ctx>()({ component: Outlet });

/** Exige sessão completa; redireciona para a etapa pendente do login quando falta algo. */
async function requireSession(ctx: Ctx) {
  try {
    return await ctx.queryClient.ensureQueryData(meQuery);
  } catch (err) {
    if (isAuthError(err)) {
      const step = pendingStep.get();
      throw redirect({ to: step === 'mfa_setup' ? '/entrar/autenticador' : step === 'mfa_verify' ? '/entrar/verificacao' : '/entrar' });
    }
    throw err;
  }
}

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/entrar', component: LoginPage });
const mfaSetupRoute = createRoute({ getParentRoute: () => rootRoute, path: '/entrar/autenticador', component: MfaSetupPage });
const mfaVerifyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/entrar/verificacao', component: MfaVerifyPage });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/convite', component: AcceptInvitePage });
const forgotRoute = createRoute({ getParentRoute: () => rootRoute, path: '/entrar/esqueci', component: ForgotPasswordPage });
const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: '/redefinir-senha', component: ResetPasswordPage });
const recoveryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/entrar/codigos-de-recuperacao', component: RecoveryCodesPage });

const selectOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/selecionar-clinica',
  beforeLoad: async ({ context }) => {
    if (isAdminHost()) throw redirect({ to: '/clinicas' });
    await requireSession(context);
  },
  component: SelectOrganizationPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context }) => {
    if (isAdminHost()) throw redirect({ to: '/clinicas' });
    const me = await requireSession(context);
    if (!me.activeOrganizationId) throw redirect({ to: '/selecionar-clinica' });
  },
  pendingComponent: () => <div className="grid min-h-dvh place-items-center"><Spinner className="size-6 text-fg-subtle" label="Carregando" /></div>,
  component: AppShell,
});
const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });
const accountRoute = createRoute({ getParentRoute: () => appRoute, path: '/perfil', component: AccountPage });
const inboxRoute = createRoute({
  getParentRoute: () => appRoute, path: '/conversas',
  validateSearch: (s: Record<string, unknown>): InboxSearch => ({
    ...(['waiting', 'mine', 'open', 'closed'].includes(s.filtro as string) ? { filtro: s.filtro as InboxSearch['filtro'] } : {}),
    ...(typeof s.c === 'string' ? { c: s.c } : {}),
  }),
  component: InboxPage,
});
const leadsRoute = createRoute({
  getParentRoute: () => appRoute, path: '/leads',
  validateSearch: (s: Record<string, unknown>): LeadsSearch => ({
    ...(typeof s.lead === 'string' ? { lead: s.lead } : {}),
    ...(s.meus === true || s.meus === 'true' ? { meus: true } : {}),
    ...(typeof s.origem === 'string' ? { origem: s.origem } : {}),
    ...(typeof s.etiqueta === 'string' ? { etiqueta: s.etiqueta } : {}),
  }),
  component: LeadsPage,
});
const agendaRoute = createRoute({
  getParentRoute: () => appRoute, path: '/agenda',
  validateSearch: (s: Record<string, unknown>): AgendaSearch => ({
    ...(typeof s.dia === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.dia) ? { dia: s.dia } : {}),
    ...(typeof s.consulta === 'string' ? { consulta: s.consulta } : {}),
  }),
  component: AgendaPage,
});
const automationsRoute = createRoute({ getParentRoute: () => appRoute, path: '/automacoes', component: AutomationsPage });
const automationEditRoute = createRoute({
  getParentRoute: () => appRoute, path: '/automacoes/$id',
  validateSearch: (s: Record<string, unknown>): { modelo?: number } => (typeof s.modelo === 'number' ? { modelo: s.modelo } : {}),
  component: function AutomationEdit() {
    const { id } = automationEditRoute.useParams();
    const { modelo } = automationEditRoute.useSearch();
    return <AutomationEditorPage key={`${id}-${modelo ?? ''}`} id={id} templateIndex={modelo} />;
  },
});
const broadcastsRoute = createRoute({ getParentRoute: () => appRoute, path: '/disparos', component: BroadcastsPage });
const broadcastRoute = createRoute({
  getParentRoute: () => appRoute, path: '/disparos/$id',
  component: function BroadcastView() { const { id } = broadcastRoute.useParams(); return <BroadcastPage key={id} id={id} />; },
});
const patientsRoute = createRoute({ getParentRoute: () => appRoute, path: '/pacientes', component: PatientsPage });
const patientRoute = createRoute({
  getParentRoute: () => appRoute, path: '/pacientes/$id',
  validateSearch: (s: Record<string, unknown>): { aba?: Exclude<PatientTab, 'resumo'> } =>
    (s.aba === 'anamnese' || s.aba === 'avaliacao' || s.aba === 'fotos' || s.aba === 'documentos' || s.aba === 'cirurgia' ? { aba: s.aba } : {}),
  component: function PatientView() {
    const { id } = patientRoute.useParams();
    const { aba } = patientRoute.useSearch();
    return <PatientPage key={id} id={id} tab={aba ?? 'resumo'} />;
  },
});
const surgeriesRoute = createRoute({ getParentRoute: () => appRoute, path: '/cirurgias', component: SurgeriesPage });
const postopRoute = createRoute({ getParentRoute: () => appRoute, path: '/pos-operatorio', component: PostopPage });
const chargesRoute = createRoute({ getParentRoute: () => appRoute, path: '/cobrancas', component: ChargesPage });
const quotesRoute = createRoute({ getParentRoute: () => appRoute, path: '/orcamentos', component: QuotesPage });
const newQuoteRoute = createRoute({
  getParentRoute: () => appRoute, path: '/orcamentos/novo',
  validateSearch: (s: Record<string, unknown>): { avaliacao?: string } => (typeof s.avaliacao === 'string' ? { avaliacao: s.avaliacao } : {}),
  component: function NewQuote() {
    const { avaliacao } = newQuoteRoute.useSearch();
    return avaliacao ? <NewQuotePage key={avaliacao} evaluationId={avaliacao} /> : <QuotesPage />;
  },
});
const quoteRoute = createRoute({
  getParentRoute: () => appRoute, path: '/orcamentos/$id',
  component: function QuoteView() { const { id } = quoteRoute.useParams(); return <QuotePage key={id} id={id} />; },
});
const clinicSettingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/configuracoes', component: SettingsLayout });
const teamRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'equipe', component: TeamSettingsPage });
const brandRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'marca', component: BrandSettingsPage });
const tagsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'etiquetas', component: TagsSettingsPage });
const repliesRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'respostas-rapidas', component: QuickRepliesSettingsPage });
const distributionRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'distribuicao', component: DistributionSettingsPage });
const pricesRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'precos', component: PricesSettingsPage });
const documentsSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'documentos', component: DocumentsSettingsPage });
const paymentsSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'pagamentos', component: PaymentsSettingsPage });
const channelsSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'canais', component: ChannelsSettingsPage });
const conversionsSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'conversoes', component: ConversionsSettingsPage });
const formsSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'formularios', component: FormsSettingsPage });
const unitSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'unidade', component: UnitSettingsPage });
const postopSettingsRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: 'pos-operatorio', component: PostopSettingsPage });
const settingsIndexRoute = createRoute({ getParentRoute: () => clinicSettingsRoute, path: '/', component: () => null });
const kbRoute = createRoute({ getParentRoute: () => appRoute, path: '/base-de-conhecimento', component: KbPage });
const signaturePrintRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/documentos/$id/imprimir',
  beforeLoad: async ({ context }) => { await requireSession(context); },
  component: function SignaturePrint() { const { id } = signaturePrintRoute.useParams(); return <SignaturePrintPage id={id} />; },
});
// SOMENTE DEV: simulador do celular do paciente (a rota nem existe no build de produção)
const devWhatsAppRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dev/whatsapp', component: WhatsAppSimulatorPage });

// ------------------------------------------------------------ console da plataforma (console.*)
const noAccessRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sem-acesso', component: NoPlatformAccess });
const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-platform',
  beforeLoad: async ({ context }) => {
    if (!isAdminHost()) throw redirect({ to: '/' });
    const me = await requireSession(context);
    if (!me.isPlatformAdmin) throw redirect({ to: '/sem-acesso' });
  },
  pendingComponent: () => <div className="grid min-h-dvh place-items-center"><Spinner className="size-6 text-fg-subtle" label="Carregando" /></div>,
  component: PlatformShell,
});
const orgsRoute = createRoute({
  getParentRoute: () => platformRoute, path: '/clinicas',
  loader: ({ context }) => context.queryClient.ensureQueryData(orgsQuery),
  component: OrganizationsListPage,
});
const orgNewRoute = createRoute({ getParentRoute: () => platformRoute, path: '/clinicas/nova', component: OrganizationNewPage });
const orgDetailRoute = createRoute({
  getParentRoute: () => platformRoute, path: '/clinicas/$id',
  validateSearch: (s: Record<string, unknown>): { aba?: DetailTab } =>
    (['geral', 'equipe', 'marca', 'whatsapp'] as const).includes(s.aba as DetailTab) ? { aba: s.aba as DetailTab } : {},
  loader: ({ context, params }) => context.queryClient.ensureQueryData(orgQuery(params.id)),
  component: OrganizationDetailPage,
});

const platformProfileRoute = createRoute({ getParentRoute: () => platformRoute, path: '/perfil', component: AccountPage });
const settingsRoute = createRoute({
  getParentRoute: () => platformRoute, path: '/ajustes', // /configuracoes é da clínica (mesmo roteador): o console usa outro caminho
  loader: ({ context }) => context.queryClient.ensureQueryData(emailSettingsQuery),
  component: EmailSettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute, mfaSetupRoute, mfaVerifyRoute, recoveryRoute, inviteRoute, forgotRoute, resetRoute, selectOrgRoute, noAccessRoute, signaturePrintRoute,
  appRoute.addChildren([homeRoute, accountRoute, inboxRoute, leadsRoute, agendaRoute, kbRoute, automationsRoute, automationEditRoute, broadcastsRoute, broadcastRoute, patientsRoute, patientRoute, quotesRoute, newQuoteRoute, quoteRoute, chargesRoute, surgeriesRoute, postopRoute,
    clinicSettingsRoute.addChildren([settingsIndexRoute, teamRoute, brandRoute, tagsRoute, repliesRoute, distributionRoute, pricesRoute, paymentsSettingsRoute, documentsSettingsRoute, channelsSettingsRoute, conversionsSettingsRoute, formsSettingsRoute, unitSettingsRoute, postopSettingsRoute])]),
  ...(import.meta.env.DEV ? [devWhatsAppRoute] : []),
  platformRoute.addChildren([orgsRoute, orgNewRoute, orgDetailRoute, settingsRoute, platformProfileRoute]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', scrollRestoration: true });
}

declare module '@tanstack/react-router' {
  interface Register { router: ReturnType<typeof createAppRouter> }
}
